// React hook to manage Progress Submissions data for Gantt Actual Timeline
// - Caches approved submissions index per project
// - Provides helpers to check if a task has approved submissions and to fetch task items with attachments

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import api from '../../../../services/api';

export function useProgressSubmissions(projectId) {
  const [loadingIndex, setLoadingIndex] = useState(false);
  const [error, setError] = useState(null);
  const approvedTaskIdsRef = useRef(new Set());
  const latestApprovedDateRef = useRef(new Map()); // key: taskId -> Date
  const earliestApprovedDateRef = useRef(new Map()); // key: taskId -> Date
  const workDatesRef = useRef(new Map()); // key: taskId -> Date[] sorted unique
  const totalVolumeRef = useRef(new Map()); // key: taskId -> number (sum of volumes with valid work date)
  const lastProjectRef = useRef(null);

  // Parse a raw date (string/Date) into local Date at 00:00:00
  // This avoids timezone shift from ISO Z (e.g., 2025-05-25T00:00:00.000Z -> 24 Mei in +7)
  const toLocalDate = useCallback((raw) => {
    if (!raw) return null;
    try {
      if (raw instanceof Date) {
        return new Date(raw.getFullYear(), raw.getMonth(), raw.getDate(), 0, 0, 0, 0);
      }
      if (typeof raw === 'string') {
        // If string contains time (ISO with T/Z), parse then take local Y-M-D
        if (/T/.test(raw)) {
          const dt = new Date(raw);
          if (!isNaN(dt)) return new Date(dt.getFullYear(), dt.getMonth(), dt.getDate(), 0, 0, 0, 0);
        } else {
          // Treat as YYYY-MM-DD
          const parts = raw.split('-');
          if (parts.length === 3) {
            const y = Number(parts[0]), m = Number(parts[1]) - 1, d = Number(parts[2]);
            if (Number.isFinite(y) && Number.isFinite(m) && Number.isFinite(d)) {
              return new Date(y, m, d, 0, 0, 0, 0);
            }
          }
          const dt = new Date(raw);
          if (!isNaN(dt)) return new Date(dt.getFullYear(), dt.getMonth(), dt.getDate(), 0, 0, 0, 0);
        }
        return null;
      }
      const dt = new Date(raw);
      if (!isNaN(dt)) return new Date(dt.getFullYear(), dt.getMonth(), dt.getDate(), 0, 0, 0, 0);
      return null;
    } catch (_) {
      return null;
    }
  }, []);

  // helper to normalize URL for attachments
  const buildAttachmentUrl = useCallback((fileUrl) => {
    if (!fileUrl) return '';
    // Absolute URLs: return as-is
    if (/^https?:\/\//i.test(fileUrl)) return fileUrl;
    // Rule: if starts with /api/, map to prod dev API uploads host
    // Example: /api/uploads/<filename> -> https://api-dev.prefab-hakiki.id/uploads/<filename>
    if (fileUrl.startsWith('/api/')) {
      const filename = fileUrl.replace(/^\/api\/uploads\//, '').replace(/^\/+/, '');
      return `https://api-dev.prefab-hakiki.id/uploads/${filename}`;
    }
    // Otherwise, treat as relative upload path under /uploads
    return `https://api-dev.prefab-hakiki.id/uploads/${fileUrl.replace(/^\/+/, '')}`;
  }, []);

  const listByProject = useCallback(async (pid, { limit = 1000, status } = {}) => {
    const params = new URLSearchParams();
    if (pid) params.set('projectId', pid);
    if (status) params.set('status', status);
    if (limit) params.set('limit', String(limit));
    const { data } = await api.get(`/progress-submissions?${params.toString()}`);
    return data?.progressSubmissions || [];
  }, []);

  const loadApprovedIndex = useCallback(async (pid = projectId) => {
    if (!pid) return;
    setLoadingIndex(true);
    setError(null);
    try {
      const rows = await listByProject(pid, { limit: 1000, status: 'approved' });
      const setIds = new Set();
      const latestMap = new Map();
      const earliestMap = new Map();
      const datesMap = new Map();
      const totalsMap = new Map();
      rows.forEach(r => {
        const tid = String(r.taskId);
        // Strictly use progress work date; support multiple casings from backend
        const rawDate = r.progressWorkDate || r.progressworkdate;
        const d = toLocalDate(rawDate);
        const prev = latestMap.get(tid);
        const prevEarliest = earliestMap.get(tid);
        if (d && !isNaN(d)) {
          // Index tasks that have progressWorkDate
          setIds.add(tid);
          if (!prev || d > prev) latestMap.set(tid, d);
          if (!prevEarliest || d < prevEarliest) earliestMap.set(tid, d);
          if (!datesMap.has(tid)) datesMap.set(tid, []);
          datesMap.get(tid).push(d);
          // accumulate volume for items with valid work date
          const vol = Number(r.volume);
          if (Number.isFinite(vol)) {
            totalsMap.set(tid, (totalsMap.get(tid) || 0) + vol);
          }
        }
      });
      // Normalize unique sorted dates per task (pure local Y-M-D, no ISO)
      datesMap.forEach((arr, key) => {
        const byKey = new Map();
        arr.forEach(dt => {
          if (!(dt instanceof Date) || isNaN(dt)) return;
          const y = dt.getFullYear();
          const m = dt.getMonth();
          const d = dt.getDate();
          const k = `${y}-${String(m+1).padStart(2,'0')}-${String(d).padStart(2,'0')}`;
          if (!byKey.has(k)) byKey.set(k, new Date(y, m, d, 0, 0, 0, 0));
        });
        const uniq = Array.from(byKey.values()).sort((a,b) => a - b);
        datesMap.set(key, uniq);
      });
      approvedTaskIdsRef.current = setIds;
      latestApprovedDateRef.current = latestMap;
      earliestApprovedDateRef.current = earliestMap;
      workDatesRef.current = datesMap;
      totalVolumeRef.current = totalsMap;
      lastProjectRef.current = String(pid);
    } catch (e) {
      setError(e);
      approvedTaskIdsRef.current = new Set();
      latestApprovedDateRef.current = new Map();
      earliestApprovedDateRef.current = new Map();
      workDatesRef.current = new Map();
      totalVolumeRef.current = new Map();
    } finally {
      setLoadingIndex(false);
    }
  }, [projectId, listByProject, toLocalDate]);

  // Auto-load when project changes
  useEffect(() => {
    if (!projectId) return;
    if (String(lastProjectRef.current) !== String(projectId)) {
      loadApprovedIndex(projectId);
    }
  }, [projectId, loadApprovedIndex]);

  const hasApprovedForTask = useCallback((taskId) => {
    if (!taskId) return false;
    return approvedTaskIdsRef.current.has(String(taskId));
  }, []);

  const getLatestApprovedDate = useCallback((taskId) => {
    return latestApprovedDateRef.current.get(String(taskId)) || null;
  }, []);

  const getEarliestApprovedDate = useCallback((taskId) => {
    return earliestApprovedDateRef.current.get(String(taskId)) || null;
  }, []);

  const getWorkDatesForTask = useCallback((taskId) => {
    return workDatesRef.current.get(String(taskId)) || [];
  }, []);

  const getTotalVolumeForTask = useCallback((taskId) => {
    const n = totalVolumeRef.current.get(String(taskId));
    return Number.isFinite(n) ? n : 0;
  }, []);

  const getItemsByTask = useCallback(async (taskId, { limit = 1000 } = {}) => {
    if (!projectId || !taskId) return [];
    // Fetch approved items for dialog and resolve attachments
    const all = await listByProject(projectId, { limit, status: 'approved' });
    const filtered = all.filter(r => String(r.taskId) === String(taskId));
    const detailed = await Promise.all(filtered.map(async (s) => {
      try {
        const { data } = await api.get(`/progress-submissions/${s.id}`);
        const attachments = (data?.attachments || []).map(a => ({
          ...a,
          url: buildAttachmentUrl(a.fileUrl)
        }));
        return { ...s, attachments };
      } catch (_) {
        return { ...s, attachments: [] };
      }
    }));
    return detailed;
  }, [projectId, buildAttachmentUrl, listByProject]);

  return useMemo(() => ({
    loadingIndex,
    error,
    hasApprovedForTask,
    getLatestApprovedDate,
    getEarliestApprovedDate,
    getWorkDatesForTask,
    getTotalVolumeForTask,
    getItemsByTask,
    reloadApprovedIndex: () => loadApprovedIndex(projectId),
  }), [loadingIndex, error, hasApprovedForTask, getLatestApprovedDate, getEarliestApprovedDate, getWorkDatesForTask, getTotalVolumeForTask, getItemsByTask, loadApprovedIndex, projectId]);
}
